import { useCallback, useMemo, useRef } from 'react';
import { useMutation, useQueryClient, type MutateOptions, type UseMutationOptions } from '@tanstack/react-query';
import { ApiFailure, isStaleWrite, type EntityTag } from '@signalvote/shared';
import type { DataLayerConfig } from './defineQuery';
import {
  asFailure,
  isTransient,
  tagStale,
  transientRetryDelay,
  TRANSIENT_RETRY_LIMIT,
  withPayload,
  type Payloads,
  type RefusalParsers,
  type WriteFailure,
} from './failure';
import { applyEdits, landEdits, refresh, restoreApplied, settleApplied, type Applied, type Edit, type Refresh } from './patch';
import { invalidateWrites } from './reads';
import { rowLedger, type RowLedger, type RowTurn } from './versionStore';

/**
 * A row as a read returned it. Its token is a `RowVersion` for the entities on the server's
 * version doors and a plain number for the int-token ones.
 */
export interface VersionedRow<V extends number = number> {
  readonly id: string;
  readonly concurrencyVersion: V;
}

export type RowWriteInput<V extends number, B, T extends VersionedRow<V> = VersionedRow<V>> = { row: T } & B;

interface Common<I, R, Pl extends Payloads> {
  /** Cache edits to show on click, from the input. Rolled back on failure. */
  patch?: (input: I) => readonly Edit[];
  /** Cache edits from the response. */
  land?: (result: R, input: I) => readonly Edit[];
  /** The codes this write refuses with and how each body becomes a payload. */
  refusals?: RefusalParsers<Pl>;
  /** Retry quietly when no response arrived. */
  retry?: 'transient';
  /** Skip the campaign pin: for the campaign switch itself. */
  campaign?: 'none';
  /**
   * For a local store, or a read the server's answer can miss naming (a write that found its work
   * already done by another door). `refresh` keeps a fetch the write set already started.
   */
  onSuccess?: (result: R, input: I, refresh: Refresh) => void;
}

export interface WriteDefinition<I, R, Pl extends Payloads> extends Common<I, R, Pl> {
  row?: never;
  send: (input: I) => Promise<R>;
  /** Writes with the same lane key run one at a time, in the order they were asked for. */
  serial?: string | ((input: I) => string);
  /**
   * The row a stale failure is about, for a write that guards a row's token but can't take the
   * row arm (its answer carries no token). The reads declaring that entity then refresh.
   */
  staleRow?: (input: I) => { entity: EntityTag; id: string };
}

/**
 * A write to one versioned row. `send` must throw an error carrying the server's `code` and
 * `currentVersion` on a 409. It returns the written row, or nothing when the write leaves no
 * row (a delete). `row` is the row as this client last knew it when the write ran, so a body
 * that re-sends fields the edit leaves alone must take them from `row`: a queued write sent
 * after another write to the same row would otherwise send the earlier write's fields back as
 * they were when it was clicked.
 */
export interface RowWriteDefinition<
  E extends EntityTag,
  V extends number,
  B,
  R extends { concurrencyVersion: V } | void,
  Pl extends Payloads,
  T extends VersionedRow<V>,
> extends Common<RowWriteInput<V, B, T>, R, Pl> {
  row: E;
  send: (id: string, concurrencyVersion: V, body: B, row: T) => Promise<R>;
}

export interface WriteResult<I, R, Pl extends Payloads> {
  mutate: (input: I, options?: MutateOptions<R, Error, I>) => void;
  mutateAsync: (input: I, options?: MutateOptions<R, Error, I>) => Promise<R>;
  isPending: boolean;
  failure: WriteFailure<Pl> | null;
  variables: I | undefined;
  data: R | undefined;
  reset: () => void;
}

export type WriteHook<I, R, Pl extends Payloads> = () => WriteResult<I, R, Pl>;

/**
 * The lifecycle a row write's hook still takes, for the hand cache work around reads that are not
 * defined yet (the call-time Prospects book, for `useCloseCommitment`). It goes when those reads
 * are, so a new write declares `patch`, `land` and `onSuccess` instead.
 */
export type RowLifecycle<I, R, C> = Pick<UseMutationOptions<R, Error, I, C>, 'onMutate' | 'onError' | 'onSuccess' | 'onSettled'>;

export type RowWriteHook<V extends number, B, R, Pl extends Payloads, T extends VersionedRow<V>> = <C = unknown>(
  lifecycle?: RowLifecycle<RowWriteInput<V, B, T>, R, C>,
) => WriteResult<RowWriteInput<V, B, T>, R, Pl>;

/** One write from the moment it was asked for: its input, the campaign it was pinned to, its lane place and its cache patches. */
interface Started<I> {
  readonly input: I;
  readonly pinned: string | null;
  readonly place: RowTurn | null;
  applied: Applied[];
}

/** What a definition contributes to the engine, fixed when it is defined so the hook's callbacks stay stable. */
interface Engine<I, R, Pl extends Payloads> {
  readonly def: Common<I, R, Pl>;
  readonly run: (ledger: RowLedger, input: I) => Promise<R>;
  readonly reserve: (ledger: RowLedger, input: I) => RowTurn | null;
}

function campaignChanged(): ApiFailure {
  return new ApiFailure(0, null, 'The selected campaign changed before this write was sent', null, {}, { kind: 'campaign-changed' });
}

function unwrap<I, R, C>(options: MutateOptions<R, Error, I>): MutateOptions<R, Error, Started<I>, C> {
  const { onSuccess, onError, onSettled } = options;
  return {
    onSuccess: onSuccess && ((data, started, result, context) => onSuccess(data, started.input, result, context)),
    onError: onError && ((error, started, result, context) => onError(error, started.input, result, context)),
    onSettled: onSettled && ((data, error, started, result, context) => onSettled(data, error, started.input, result, context)),
  };
}

function currentVersionOf(error: object): number | null {
  if (error instanceof ApiFailure) return error.reason.kind === 'stale' ? error.reason.currentVersion : null;
  const version = (error as { currentVersion?: unknown }).currentVersion;
  return typeof version === 'number' ? version : null;
}

export function createDefineWrite(config: DataLayerConfig) {
  function useWrite<I, R, Pl extends Payloads, C>(engine: Engine<I, R, Pl>, lifecycle?: RowLifecycle<I, R, C>): WriteResult<I, R, Pl> {
    const client = useQueryClient();
    const ledger = rowLedger(client);
    const campaign = useRef<string | null>(null);
    campaign.current = config.useCampaignId() ?? null;
    const { def, run, reserve } = engine;

    const mutation = useMutation<R, Error, Started<I>, C>({
      ...(def.retry === 'transient'
        ? { retry: (count: number, error: unknown) => count < TRANSIENT_RETRY_LIMIT && isTransient(error), retryDelay: transientRetryDelay }
        : {}),
      onMutate: async (started, context) => {
        const result = (await lifecycle?.onMutate?.(started.input, context)) as C;
        started.applied = await applyEdits(client, started.pinned, def.patch?.(started.input) ?? []);
        return result;
      },
      mutationFn: async (started) => {
        if (started.place) await started.place.turn;
        if (def.campaign !== 'none' && campaign.current !== started.pinned) throw campaignChanged();
        let result: R;
        try {
          result = await run(ledger, started.input);
        } catch (error) {
          // Only a door turns a rejected fetch into an unreachable failure; any other throw,
          // a TypeError from a send's own code included, is a bug and surfaces as one.
          throw error instanceof ApiFailure ? withPayload(error, def.refusals) : error;
        }
        landEdits(client, started.pinned, def.land?.(result, started.input) ?? [], Date.now());
        return result;
      },
      onSuccess: (data, started, result, context) => {
        settleApplied(client, started.applied);
        def.onSuccess?.(data, started.input, (query, params) => refresh(client, query, params, { restart: false }));
        return lifecycle?.onSuccess?.(data, started.input, result, context);
      },
      onError: (error, started, result, context) => {
        const reason = asFailure(error)?.reason;
        const kind = reason?.kind;
        const refreshed = restoreApplied(client, started.applied, kind !== 'refused' && kind !== 'campaign-changed');
        // A lost race names no writes, so the reads holding the row would keep the refused token
        // and a retry would lose again: refresh them as a write set naming the row's entity would.
        if (reason?.kind === 'stale' && reason.entity) void invalidateWrites(client, [reason.entity as EntityTag], refreshed);
        return lifecycle?.onError?.(error, started.input, result, context);
      },
      onSettled: (data, error, started, result, context) => lifecycle?.onSettled?.(data, error, started.input, result, context),
    });

    const { mutateAsync: start, error } = mutation;
    const mutateAsync = useCallback(
      (input: I, options?: MutateOptions<R, Error, I>) => {
        const started: Started<I> = { input, pinned: campaign.current, place: reserve(ledger, input), applied: [] };
        const outcome = start(started, options && unwrap(options));
        if (started.place) void outcome.then(started.place.release, started.place.release);
        return outcome;
      },
      [start, reserve, ledger],
    );
    const mutate = useCallback(
      (input: I, options?: MutateOptions<R, Error, I>) => {
        mutateAsync(input, options).catch(() => undefined);
      },
      [mutateAsync],
    );
    const failure = useMemo(() => {
      const base = error === null ? null : asFailure(error);
      return base && withPayload(base, def.refusals);
    }, [error, def.refusals]);

    return {
      mutate,
      mutateAsync,
      isPending: mutation.isPending,
      failure,
      variables: mutation.variables?.input,
      data: mutation.data,
      reset: mutation.reset,
    };
  }

  function defineWrite<
    E extends EntityTag,
    V extends number,
    B,
    R extends { concurrencyVersion: V } | void,
    T extends VersionedRow<V> = VersionedRow<V>,
    Pl extends Payloads = {},
  >(def: RowWriteDefinition<E, V, B, R, Pl, T>): RowWriteHook<V, B, R, Pl, T>;
  function defineWrite<I = void, R = void, Pl extends Payloads = {}>(def: WriteDefinition<I, R, Pl>): WriteHook<I, R, Pl>;
  function defineWrite(
    def: WriteDefinition<unknown, unknown, Payloads> | RowWriteDefinition<EntityTag, number, unknown, { concurrencyVersion: number } | void, Payloads, VersionedRow>,
  ) {
    if (def.row === undefined) {
      const { send, serial, staleRow } = def;
      const engine: Engine<unknown, unknown, Payloads> = {
        def,
        run: async (_ledger, input) => {
          try {
            return await send(input);
          } catch (error) {
            if (staleRow && typeof error === 'object' && error !== null && isStaleWrite(error)) {
              tagStale(error, { ...staleRow(input), currentVersion: currentVersionOf(error) });
            }
            throw error;
          }
        },
        reserve: (ledger, input) => (serial === undefined ? null : ledger.lane(typeof serial === 'function' ? serial(input) : serial)),
      };
      return function useDefinedWrite() {
        return useWrite(engine);
      };
    }
    const { row: entity, send } = def;
    const engine: Engine<RowWriteInput<number, unknown>, { concurrencyVersion: number } | void, Payloads> = {
      def,
      run: async (ledger, input) => {
        const { row, ...body } = input;
        ledger.observe(entity, row.id, row.concurrencyVersion);
        const token = ledger.get(entity, row.id) as number;
        try {
          const written = await send(row.id, token, body, ledger.freshest(entity, row.id, row));
          if (written) {
            ledger.observe(entity, row.id, written.concurrencyVersion);
            ledger.wrote(entity, row.id, written);
          }
          return written;
        } catch (error) {
          // The server's current token never enters the store: the caller has not seen the
          // change it stands for, so sending it would overwrite that change unseen.
          if (typeof error === 'object' && error !== null && isStaleWrite(error)) {
            tagStale(error, { entity, id: row.id, currentVersion: currentVersionOf(error) });
          }
          throw error;
        }
      },
      reserve: (ledger, input) => ledger.reserve(entity, input.row.id),
    };
    return function useDefinedRowWrite<C>(lifecycle?: RowLifecycle<RowWriteInput<number, unknown>, { concurrencyVersion: number } | void, C>) {
      return useWrite(engine, lifecycle);
    };
  }

  return defineWrite;
}
